import { useRef, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from './icons';
import { useDialog } from './useDialog';

export interface PopupProps {
  open: boolean;
  onClose: () => void;
  /** The dialog's accessible name; the content carries its own heading. */
  label: string;
  children: ReactNode;
}

/**
 * A centred card over a dimmed page, for short self-contained content. Unlike
 * `Modal`, which takes the whole screen for lists and filters.
 */
export function Popup({ open, onClose, label, children }: PopupProps): ReactElement | null {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  useDialog(open, onClose, panelRef);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 backdrop-blur-sm">
      {/* min-h-full rather than centring the scroller itself, so a card taller
          than the screen scrolls from its top instead of being cut off. */}
      <div
        className="flex min-h-full items-center justify-center px-4 py-8"
        onClick={(event) => {
          // The backdrop only: a click inside the card bubbles up here too.
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          tabIndex={-1}
          className="relative w-full max-w-md rounded-[1.75rem] border border-ink-800 bg-ink-950 px-6 pt-10 pb-8 text-center outline-none"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="absolute top-5 right-5 text-fog-100 transition-opacity hover:opacity-70"
          >
            <CloseIcon className="size-7" />
          </button>
          {children}
        </div>
      </div>
    </div>
  );
}
